import { MenuIcons } from '../components/CardMenu';

// What the "⋯" menu on a vendor card actually does.
//
// Modelled on Trello's card menu, but two of Trello's items don't map cleanly
// onto a technician and are handled differently — see the notes on each.

// A link straight to one vendor. The app is a single page with no router, so
// this is a hash the app reads on load (see App.js) rather than a real route.
export const vendorLink = (vendor) =>
  `${window.location.origin}/#/vendor/${vendor._id}`;

// Everything on the card as plain text, ready to paste into a text message,
// an email, or a note.
const vendorAsText = (v) => {
  const p = v.profile || {};
  const lines = [
    v.businessName,
    v.phone || v.userId?.phone,
    v.email || v.userId?.email,
    p.address,
    p.expertise?.length ? `Trades: ${p.expertise.join(', ')}` : null,
    p.hourlyRate ? `Rate: ${p.hourlyRate}` : null,
    v.compliance
      ? (v.compliance.canBePaid ? 'Paperwork current' : 'Paperwork NOT current — payments blocked')
      : null,
  ];
  return lines.filter(Boolean).join('\n');
};

// navigator.clipboard needs a secure context (https, or localhost). The live
// site is https so this is the normal path; the fallback covers anything else.
async function copyText(text) {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch { /* fall through to the textarea trick */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

/**
 * Builds the menu for one vendor card.
 *
 * @param {Object} ctx
 *   onOpen      — open the vendor's full profile
 *   onEdit      — open the edit form (name, phone, address, rates…)
 *   onWatch     — toggle the eye (persisted; see PATCH /api/vendors/:id/watch)
 *   onDelete    — hand off to the existing "Confirm Deletion" dialog
 *   showToast   — feedback, since copying gives no visible signal otherwise
 */
export const vendorMenuItems = ({ onOpen, onEdit, onWatch, onDelete, onMove, showToast }) => (v, column) => [
  {
    key: 'open',
    label: 'Open card',
    icon: MenuIcons.link,
    onSelect: () => onOpen(v),
  },
  {
    // A tech fills the application in on a phone, so the details arrive with
    // typos. Fixing them from the card is quicker than opening the profile.
    key: 'edit',
    label: 'Edit details',
    icon: MenuIcons.pencil,
    hidden: !onEdit,
    onSelect: () => onEdit(v),
  },
  {
    // Trello's first menu item, and on a phone it's the only reliable way to
    // move a card: dragging inside a side-scrolling board fights the scroll
    // gesture. Dragging still works with a mouse — this is the path that
    // always works.
    key: 'move',
    label: 'Move card',
    icon: MenuIcons.move,
    hidden: !onMove,
    onSelect: () => onMove(v, column),
  },
  {
    // Trello's "Copy card" duplicates the card. Duplicating a technician would
    // create a second record for a real person — with the same email, which
    // their login is keyed on — so this copies the card's details to the
    // clipboard instead. That's the thing you actually want when you're
    // passing a tech's info to someone.
    key: 'copy',
    label: 'Copy card details',
    icon: MenuIcons.copy,
    onSelect: async () => {
      const ok = await copyText(vendorAsText(v));
      showToast(ok ? 'Card details copied' : "Couldn't copy — select and copy by hand");
    },
  },
  {
    key: 'copylink',
    label: 'Copy link',
    icon: MenuIcons.link,
    onSelect: async () => {
      const ok = await copyText(vendorLink(v));
      showToast(ok ? 'Link copied — opens this vendor' : "Couldn't copy the link");
    },
  },
  {
    // The native share sheet where the phone has one (iOS Safari, Android
    // Chrome); on a desktop browser without it this quietly becomes a copy.
    key: 'share',
    label: 'Share link',
    icon: MenuIcons.share,
    onSelect: async () => {
      const url = vendorLink(v);
      if (navigator.share) {
        try {
          await navigator.share({ title: v.businessName, url });
          return;
        } catch { return; }   // the user dismissed the sheet — not an error
      }
      const ok = await copyText(url);
      showToast(ok ? 'Link copied' : "Couldn't copy the link");
    },
  },
  {
    // Trello's Watch means "tell me when this changes". The equivalent that
    // matters here: a watched vendor pins to the top of its column, and QPS is
    // copied on that tech's paperwork-expiry reminders.
    key: 'watch',
    label: v.watched ? 'Stop watching' : 'Watch',
    icon: v.watched ? MenuIcons.eyeOff : MenuIcons.eye,
    onSelect: () => onWatch(v),
  },
  {
    key: 'delete',
    label: 'Delete card',
    icon: MenuIcons.trash,
    danger: true,
    onSelect: () => onDelete(v),
  },
];
